import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import axios from "../utils/axios";

interface BotInfo {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  avatarURL: string;
  guildCount?: number;
}

interface BotContextType {
  botInfo: BotInfo | null;
  loading: boolean;
}

const BotContext = createContext<BotContextType | undefined>(undefined);

export function BotProvider({ children }: { children: ReactNode }) {
  const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if bot info is in localStorage
    const cachedBotInfo = localStorage.getItem("botInfo");
    if (cachedBotInfo) {
      try {
        const parsed = JSON.parse(cachedBotInfo);
        setBotInfo(parsed);
        setLoading(false);
      } catch (error) {
        console.error("Error parsing cached bot info:", error);
      }
    }

    // Fetch fresh bot info from API
    axios
      .get("/api/bot/info")
      .then((response) => {
        const info = response.data;
        setBotInfo(info);
        // Cache in localStorage
        localStorage.setItem("botInfo", JSON.stringify(info));
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching bot info:", error);
        setLoading(false);
      });
  }, []);

  return (
    <BotContext.Provider value={{ botInfo, loading }}>
      {children}
    </BotContext.Provider>
  );
}

export function useBotInfo() {
  const context = useContext(BotContext);
  if (context === undefined) {
    throw new Error("useBotInfo must be used within a BotProvider");
  }
  return context;
}
